import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { Link } from 'react-router'
import { type Route } from './+types/legal-product-terms-privacy.ts'

const MetaTitle = 'Legal – Product Terms & Privacy | Dealer Pro Edge'
const MetaDescription =
	'Review the legal terms, privacy notices, and customer agreements that apply to Dealer Pro Edge products and services.'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/legal-product-terms-privacy'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'robots', content: 'noindex, nofollow' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
	]
}

// =============================================================================
// DATA
// =============================================================================

const legalSections = [
	{
		id: 'corporate',
		category: 'Corporate / Company',
		description:
			"These documents apply to Dealer Pro Edge's own website and general company services.",
		documents: [
			{
				title: 'Dealer Pro Edge Website Terms and Conditions',
				description:
					'Terms governing access to and use of dealerproedge.com and related company-operated website features.',
				href: '/website-terms-of-use',
			},
			{
				title: 'Dealer Pro Edge Privacy Policy',
				description:
					'Explains how Dealer Pro Edge collects, uses, shares, and protects personal information collected through dealerproedge.com and related company services.',
				href: '/website-privacy-policy',
			},
		],
	},
	{
		id: 'chat-widget',
		category: 'Chat Widget Package',
		description:
			'These documents apply only when the Dealer Pro Edge chat widget package is enabled on a website.',
		documents: [
			{
				title: 'Dealer Pro Edge Chat Widget Terms of Use',
				description:
					'Terms governing use of the live chat, AI-assisted chat, messaging, and related communication features made available through the Dealer Pro Edge chat widget.',
				href: '/chat-widget-terms-of-use',
			},
			{
				title: 'Dealer Pro Edge Chat Widget Privacy Notice',
				description:
					'Explains how personal information is collected, used, shared, retained, and protected through the Dealer Pro Edge chat widget.',
				href: '/chat-widget-privacy-policy',
			},
		],
	},
]

// =============================================================================
// ROUTE COMPONENT
// =============================================================================

export default function LegalProductTermsPrivacyRoute() {
	return (
		<div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
			{/* Page header */}
			<div className="mb-12">
				<h1 className="text-4xl font-bold tracking-tight text-neutral-900">
					Dealer Pro Edge Legal
				</h1>
				<p className="mt-4 text-lg leading-relaxed text-neutral-600">
					Dealer Pro Edge, LLC provides different products and services that may
					be subject to different legal terms, privacy notices, and customer
					agreements. Please review the document that applies to the website,
					product, or feature you are using.
				</p>
			</div>

			{/* Legal sections */}
			<div className="space-y-12">
				{legalSections.map((section) => (
					<section key={section.id} aria-labelledby={`section-${section.id}`}>
						{/* Section heading */}
						<div className="mb-6 border-b border-neutral-200 pb-4">
							<h2
								id={`section-${section.id}`}
								className="text-xl font-semibold text-neutral-900"
							>
								{section.category}
							</h2>
							<p className="mt-1 text-sm text-neutral-500">
								{section.description}
							</p>
						</div>

						{/* Document cards */}
						<ul className="space-y-4" role="list">
							{section.documents.map((doc) => (
								<li key={doc.title}>
									<DocumentCard
										title={doc.title}
										description={doc.description}
										href={doc.href}
									/>
								</li>
							))}
						</ul>
					</section>
				))}
			</div>
		</div>
	)
}

// =============================================================================
// DOCUMENT CARD COMPONENT
// =============================================================================

interface DocumentCardProps {
	title: string
	description: string
	href: string | null
}

function DocumentCard({ title, description, href }: DocumentCardProps) {
	const cardClasses =
		'flex items-start justify-between gap-6 rounded-lg border border-neutral-200 bg-white/80 p-6 shadow-sm backdrop-blur-sm transition-all duration-200'

	const inner = (
		<>
			<div className="flex-1">
				<p className="font-semibold text-neutral-900">{title}</p>
				<p className="mt-1 text-sm leading-relaxed text-neutral-500">
					{description}
				</p>
			</div>
			{href && (
				<span className="mt-0.5 shrink-0 text-sm font-medium text-[#EC1C23]">
					View →
				</span>
			)}
		</>
	)

	if (href) {
		return (
			<Link
				to={href}
				prefetch="intent"
				className={`${cardClasses} hover:border-[#EC1C23]/40 hover:shadow-md`}
			>
				{inner}
			</Link>
		)
	}

	return (
		<div className={`${cardClasses} opacity-60`} aria-disabled="true">
			{inner}
			<span className="mt-0.5 shrink-0 text-xs text-neutral-400">
				Coming soon
			</span>
		</div>
	)
}
